/** Exhibitor logo grid — 12-col desktop, 4-col mobile with View more. */

import { useState } from 'react';

/** Mobile layout: 4 columns × 3 rows visible before expanding. */
const MOBILE_INITIAL_COUNT = 12;

/** Logo tile size constants. */
const LOGO_SIZE_DESKTOP = '8.5rem';
/** Target logo size on mobile — fixed tile size, gaps are flexible. */
const LOGO_SIZE_MOBILE = '5.4rem';

export interface ExhibitorLogo {
    id: string;
    name: string;
    logoUrl: string;
    profileLink?: string;
    logoBackgroundColor?: string;
}

interface HDEExhibitorLogoGridProps {
    logos: ExhibitorLogo[];
    isMobile?: boolean;
}

interface LogoTileProps {
    logo: ExhibitorLogo;
}

/** Single logo tile — wraps in an anchor when a profile link is available. */
function LogoTile({ logo }: LogoTileProps) {
    const tileStyle: React.CSSProperties = {
        aspectRatio: '1 / 1',
        backgroundColor: logo.logoBackgroundColor ?? '#fff',
        border: '1.081px solid #A9A9A9',
    };

    const inner = (
        <img
            src={logo.logoUrl}
            alt={logo.name}
            className="h-full w-full object-contain"
            loading="lazy"
        />
    );

    if (logo.profileLink) {
        return (
            <a
                href={logo.profileLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center p-[0.5rem]"
                style={tileStyle}
                title={logo.name}
            >
                {inner}
            </a>
        );
    }

    return (
        <div
            className="flex items-center justify-center p-[0.5rem]"
            style={tileStyle}
            title={logo.name}
        >
            {inner}
        </div>
    );
}

function MobileLogoGrid({ logos }: { logos: ExhibitorLogo[] }) {
    const [expanded, setExpanded] = useState(false);

    const visibleLogos = expanded
        ? logos
        : logos.slice(0, MOBILE_INITIAL_COUNT);
    const hasMore = logos.length > MOBILE_INITIAL_COUNT;

    return (
        <section
            data-testid="hde-exhibitor-logo-grid"
            className="mt-10 w-full bg-white px-[1.5rem] pt-[2rem] pb-[2rem]"
        >
            <div className="flex flex-wrap gap-x-1.5 gap-y-1.5">
                {visibleLogos.map((logo) => (
                    <div
                        key={logo.id}
                        style={{ width: LOGO_SIZE_MOBILE, flexShrink: 0 }}
                    >
                        <LogoTile logo={logo} />
                    </div>
                ))}
            </div>
            {hasMore && !expanded && (
                <button
                    type="button"
                    onClick={() => setExpanded(true)}
                    className="mt-3 flex h-13 w-full items-center justify-center rounded-[var(--radius-btn-l)] border border-[#141414] bg-white"
                >
                    <span className="text-[0.9375rem] font-bold tracking-wider text-[#141414] uppercase">
                        View more
                    </span>
                </button>
            )}
        </section>
    );
}

function DesktopLogoGrid({ logos }: { logos: ExhibitorLogo[] }) {
    return (
        <section
            data-testid="hde-exhibitor-logo-grid"
            className="mt-[8rem] w-full bg-white px-[4.6rem] py-[2rem]"
        >
            <div className="flex flex-wrap gap-x-[0.65rem] gap-y-[0.74rem]">
                {logos.map((logo) => (
                    <div
                        key={logo.id}
                        style={{ width: LOGO_SIZE_DESKTOP, flexShrink: 0 }}
                    >
                        <LogoTile logo={logo} />
                    </div>
                ))}
            </div>
        </section>
    );
}

/**
 * Displays the full exhibitor logo grid for the HDE page.
 * Desktop: 12 logos per row, left-aligned, unlimited rows.
 * Mobile: 4 columns × 3 rows initially, with a View more button.
 */
export const HDEExhibitorLogoGrid = ({
    logos,
    isMobile = false,
}: HDEExhibitorLogoGridProps) => {
    if (logos.length === 0) {
        return null;
    }

    if (isMobile) {
        return <MobileLogoGrid logos={logos} />;
    }

    return <DesktopLogoGrid logos={logos} />;
};
